/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */

import { Node } from '@msdyn365-commerce-modules/utilities';
import IncrementalQuantity from './IncrementalQuantity';
import { IGridSettings, IImageSettings, IComponentProps } from '@msdyn365-commerce/core';
import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    ProductAvailableQuantity,
    ProductDimension,
    SimpleProduct,
    ProductsDataActions,
    ProductSearchCriteria,
    ProductType,
    ProductPrice
} from '@msdyn365-commerce/retail-proxy';

import { OrderDetailsProduct } from '@msdyn365-commerce/components';
import { ICartActionResult, getCartState } from '@msdyn365-commerce/global-state';
//import { IProductCollectionData } from './product-collection.data';

export interface IAddToCartResources {
    goToCartText: string;
    continueShoppingText: string;
    closeNotificationLabel: string;
    headerItemOneText: string;
    headerItemFormatText: string;
    headerLinesFormatText?: string;
    headerMessageText: string;
    freePriceText: string;
    originalPriceText: string;
    currentPriceText: string;
    addedQuantityText: string;
    buyAgainNotificationTitle?: string;
    buyAgainNotificationCloseAriaLabel?: string;
    validationErrorMessage?: string;
    oneErrorText?: string;
    multiErrorsText?: string;
}
export interface IAddToCartComponentProps extends IComponentProps<{ product: SimpleProduct; price?: ProductPrice } | undefined> {
    context: any;
    className?: string;
    product: any;
    outOfStockText?: string;
    disabled?: boolean;
    quantity?: number;
    navigationUrl?: string;
    productAvailability?: ProductAvailableQuantity;
    getSelectedProduct?: Promise<SimpleProduct | null>;
    isNavigationToCartPageDisabled?: boolean;
    shouldSkipSiteSettings?: boolean;
    addToCartArialLabel?: string;
    products?: {
        productId: number;
        quantity: number;
        catalogId?: number;
    }[];
    orderDetailsProducts?: OrderDetailsProduct[];
    hasAvailableProducts?: boolean;
    imageSettings?: IImageSettings;
    gridSettings?: IGridSettings;

    isLoading?: boolean;
    isUpdatingDimension?: boolean;
    isLoadingDeliveryOptions?: boolean;
    isUpdatingDeliveryOptions?: boolean;
    isAddServiceItemToCart?: boolean;
    isAddEmailDeliveryItemToCart?: boolean;
    isPriceKeyedIn?: boolean;
    customPriceAmount?: number;
    isOrderQuantityLimitsFeatureEnabled?: boolean;

    dialogStrings?: IAddToCartResources;

    telemetryContent?: ITelemetryContent;

    catalogId?: number;
    cartData?: any;

    isCustomPriceSelected?: boolean;
    maximumKeyInPrice?: number;
    minimumKeyInPrice?: number;
    defaultMaximumKeyInPrice?: number;
    defaultMinimumKeyInPrice?: number;
    isProductQuantityLoading?: boolean;
    isBundleCollection?: boolean;
    onAdd?(result: ICartActionResult): void;
    onError?(result: IAddToCartFailureResult): void;
    changeUpdatingDimension?(isUpdatingDimension: boolean): void;
    changeUpdatingDeliveryOptions?(isUpdatingDeliveryOptions: boolean): void;
}
export declare type ICartActionFailureReason =
    | 'EMPTYINPUT'
    | 'MISSINGDIMENSION'
    | 'OUTOFSTOCK'
    | 'CARTACTIONFAILED'
    | 'INVALIDCUSTOMAMOUNT';
export interface IAddToCartFailureResult {
    failureReason: ICartActionFailureReason;

    stockLeft?: number;
    cartActionResult?: ICartActionResult;
    missingDimensions?: ProductDimension[];
}
export interface IProductCollectionState {
    currentProductqQty?: any;
}
//const defaultQuantity = 1;

class ProductCollectionQty extends React.PureComponent<IAddToCartComponentProps, IProductCollectionState> {
    constructor(props: IAddToCartComponentProps) {
        super(props);
        this.state = {
            currentProductqQty: ''
        };
    }
    public removeITem = () => {
        void getCartState(this.props.context.actionContext).then((result: any): boolean => {
            const recordId = this.props.product.ItemId;
            void this.removeItemFromCart(this.props, recordId);
            this.setState({ currentProductqQty: -1 });
            return false;
        });
    };
    public removeItemFromCart = async (props: IAddToCartComponentProps, LineID: any): Promise<void> => {
        const cartState = await getCartState(props.context.actionContext);
        const productInfow = await this.getAddToCartInputFromProps(props);
        const cartLinesData = cartState.cart.CartLines;
        const findData = cartLinesData?.find(cartLinesData => cartLinesData.ItemId === productInfow[0].product.ItemId);
        const input = {
            cartLineIds: [findData?.LineId!]
        };
        await cartState.removeCartLines(input);
    };
    public getAddToCartInputFromProps = async (props: IAddToCartComponentProps) => {
        const defaultQuantity = 1;
        const products = props.orderDetailsProducts?.filter(product => !ObjectExtensions.isNullOrUndefined(product.simpleProduct));
        const input = products?.map(product => {
            return {
                product: product.simpleProduct!,
                count: ObjectExtensions.isNullOrUndefined(product.salesLine.Quantity) ? defaultQuantity : product.salesLine.Quantity,
                catalogId: product.salesLine.CatalogId
            };
        });

        if (ArrayExtensions.hasElements(input)) {
            return input;
        }

        if (ArrayExtensions.hasElements(props.products)) {
            const actionContext = props.context.actionContext;
            const apiSettings = actionContext.requestContext.apiSettings;

            const catalogId = props.catalogId;
            const searchCriteriaInput: ProductSearchCriteria = {
                Context: {
                    ChannelId: apiSettings.channelId,
                    CatalogId: catalogId
                },
                IncludeAttributes: false,
                Ids: props.products.map(product => product.productId)
            };

            const searchResult = await ProductsDataActions.searchByCriteriaAsync({ callerContext: actionContext }, searchCriteriaInput);
            const productSearchResultsWithImages = searchResult.map(productSearchResult => {
                const newProductSearchResult = { ...productSearchResult };
                const newImageUrl = ' generateProductImageUrl(newProductSearchResult, apiSettings);';

                if (newImageUrl) {
                    newProductSearchResult.PrimaryImageUrl = newImageUrl;
                }

                return newProductSearchResult;
            });

            return props.products.map(item => {
                const productData = productSearchResultsWithImages.find(product => product.RecordId === item.productId);
                let simpleProduct: SimpleProduct;
                if (productData) {
                    simpleProduct = {
                        ...productData,
                        ProductTypeValue: ProductType.Variant,
                        AdjustedPrice: productData.Price,
                        BasePrice: productData.BasePrice ?? productData.Price
                    };
                } else {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Cast to simple product since only record id is available.
                    simpleProduct = {
                        RecordId: item.productId
                    } as SimpleProduct;
                }

                return {
                    product: simpleProduct,
                    count: item.quantity,
                    catalogId: item.catalogId
                };
            });
        }

        if (props.data?.product) {
            return [
                {
                    product: props.data.product,
                    count: props.quantity ?? defaultQuantity,
                    catalogId: props.catalogId
                }
            ];
        }

        return [];
    };
    public onChangeHandler = async (newValue: number): Promise<boolean> => {
        if (newValue === -1) {
            this.removeITem();
        }
        const productID = this.props.data?.product.RecordId;
        let totalAvailableQuantity = 0;
        if (productID) {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const axios = require('axios');
            const data = JSON.stringify({
                searchCriteria: {
                    ProductIds: [productID],
                    QuantityUnitTypeValue: 2,
                    DefaultWarehouseOnly: true
                }
            });

            const config = {
                method: 'post',
                maxBodyLength: Infinity,
                // url: 'https://scudisf0ajx86631475-rs.su.retail.dynamics.com/Commerce/Products/GetEstimatedAvailability?api-version=7.3',
                url: 'https://scuh5bsct3g80746264-rs.su.retail.dynamics.com/Commerce/Products/GetEstimatedAvailability?api-version=7.3',
                headers: {
                    accept: 'application/json',
                    'accept-language': 'en-US',
                    'cache-control': 'no-cache',
                    'content-type': 'application/json',
                    oun: '00000001'
                },
                data: data
            };

            await axios
                .request(config)
                .then((response: { data: any }) => {
                    if (
                        response.data?.ProductWarehouseInventoryAvailabilities &&
                        response.data?.ProductWarehouseInventoryAvailabilities.length > 0
                    ) {
                        const respObject = response.data?.ProductWarehouseInventoryAvailabilities.filter((item: any) => {
                            return item.ProductId === productID;
                        });
                        if (respObject && respObject.length > 0) {
                            totalAvailableQuantity = Math.floor((Number(respObject[0]?.PhysicalAvailable) * 100) / 100);
                        }
                    }
                })
                .catch((error: any) => {
                    console.log(error);
                });
        }
        if (newValue > totalAvailableQuantity) {
            return false;
        }
        const cartState = getCartState(this.props.context.actionContext).then((result: any): boolean => {
            const recordId = this.props.product.ItemId;
            const cartLinesData = result._cart.CartLines;
            const findData = cartLinesData.find((cartLinesData: { ItemId: any }) => cartLinesData.ItemId === recordId);
            this.setState({ currentProductqQty: newValue });
            if (findData === undefined) {
                //   console.log(findData.Quantity);
                void this.addOneItemToCart(this.props);
                return true;
            }
            if (!result.isEmpty) {
                void this.updateCartId(this.props, findData.Quantity, newValue);
                return true;
            } else {
                void this.addOneItemToCart(this.props);
                //return true;
            }
            return false;
        });
        console.log(cartState);

        return true;
    };
    public updateCartId = async (props: IAddToCartComponentProps, oldQty: any, newQty: any): Promise<void> => {
        const cartState = await getCartState(props.context.actionContext);
        const productInfow = await this.getAddToCartInputFromProps(props);
        const cartLinesData = cartState.cart.CartLines;
        const findData = cartLinesData?.find(cartLinesData => cartLinesData.ItemId === productInfow[0].product.ItemId);
        const addToCartResult = await cartState.updateCartLineQuantity({
            cartLineId: findData?.LineId || '',
            newQuantity: newQty
        });
        console.log(addToCartResult);
    };
    /**
     * Add one item to cart when it is not buy again.
     * @param props -- The props.
     * @param setDisabled -- The set disable call back.
     * @param openModal -- The open modal call back.
     */
    public addOneItemToCart = async (props: IAddToCartComponentProps): Promise<void> => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
        const addToCartInput = await this.getAddToCartInputFromProps(props);
        const product = addToCartInput[0].product;
        const quantity = addToCartInput[0].count;

        const cartState = await getCartState(props.context.actionContext);
        const addToCartResult = await cartState.addProductToCart({
            product,
            count: quantity,
            availableQuantity: props.productAvailability?.AvailableQuantity,
            additionalProperties: { orderQuantityLimitsFeatureIsEnabled: props.isOrderQuantityLimitsFeatureEnabled },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
            enableStockCheck: props.context.app.config.enableStockCheck,
            isPriceKeyedIn: props.isPriceKeyedIn,
            customPrice: props.isPriceKeyedIn ? props.customPriceAmount : undefined,
            isAddEmailDeliveryItemToCart: props.isAddEmailDeliveryItemToCart,
            shouldSkipSiteSettings: props.shouldSkipSiteSettings,
            catalogId: addToCartInput[0].catalogId
        });

        if (addToCartResult.status === 'SUCCESS') {
            console.log('its success');
        }
    };
    public _getCartStatusUpdate = (props: IAddToCartComponentProps, recordId: number): number => {
        const cartLinesData = props.cartData;
        if (cartLinesData.length !== 0) {
            const findData = cartLinesData.find((cartLinesData: { ItemId: any }) => cartLinesData.ItemId === recordId);
            if (findData) {
                return findData.Quantity;
            }
        }

        return 0;
        //return findData?.Quantity || 0;
    };
    public _SingleAddToCart = () => {
        void this.addOneItemToCart(this.props);
        this.setState({ currentProductqQty: 1 });
        return true;
    };

    public render(): JSX.Element | null {
        const isBundleCollection = this.props.isBundleCollection ? this.props.isBundleCollection : false;
        var getQty = this._getCartStatusUpdate(this.props, this.props.product.ItemId);
        //this.setState({ currentProductqQty: getQty });
        var button = null;
        var buttonAddtoCart = null;
        if (this.state.currentProductqQty === -1) {
            getQty = 0;
        }
        //this.setState({ currentProductqQty: getQty });
        if (
            getQty === 0 &&
            (this.state.currentProductqQty === 0 || this.state.currentProductqQty === '' || this.state.currentProductqQty === -1)
        ) {
            getQty = 0;
            // this.setState({ currentProductqQty: 0 });
            buttonAddtoCart = (
                <>
                    {isBundleCollection ? (
                        <button className='local-bundle-plus-icon' onClick={this._SingleAddToCart}>
                            <svg width='248' height='56' viewBox='0 0 248 56' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                <g filter='url(#filter0_d_840_2160)'>
                                    <rect
                                        x='4'
                                        y='2'
                                        width='240'
                                        height='48'
                                        rx='24'
                                        fill='white'
                                        fill-opacity='0.95'
                                        shape-rendering='crispEdges'
                                    />
                                    <rect x='4.5' y='2.5' width='239' height='47' rx='23.5' stroke='#610FD1' shape-rendering='crispEdges' />
                                </g>
                                <path
                                    d='M90.3124 32L89.4484 29.536H84.9844L84.1044 32H81.6564L85.9764 20.512H88.6324L92.9204 32H90.3124ZM87.2404 23.168L85.6724 27.6H88.7764L87.2404 23.168ZM92.9236 27.728C92.9236 25.2 94.4116 23.296 96.7156 23.296C97.7396 23.296 98.6036 23.744 99.2436 24.56V20.512H101.452V32H99.4196V30.768C98.7476 31.664 97.8196 32.16 96.7156 32.16C94.4116 32.16 92.9236 30.224 92.9236 27.728ZM95.2116 27.728C95.2116 29.328 96.1236 30.176 97.2116 30.176C98.3156 30.176 99.2276 29.312 99.2276 27.696C99.2276 26.08 98.2996 25.264 97.2116 25.264C96.1236 25.264 95.2116 26.096 95.2116 27.728ZM102.717 27.728C102.717 25.2 104.205 23.296 106.509 23.296C107.533 23.296 108.397 23.744 109.037 24.56V20.512H111.245V32H109.213V30.768C108.541 31.664 107.613 32.16 106.509 32.16C104.205 32.16 102.717 30.224 102.717 27.728ZM105.005 27.728C105.005 29.328 105.917 30.176 107.005 30.176C108.109 30.176 109.021 29.312 109.021 27.696C109.021 26.08 108.093 25.264 107.005 25.264C105.917 25.264 105.005 26.096 105.005 27.728ZM121.566 29.84V31.776C121.022 32.048 120.51 32.16 119.854 32.16C118.094 32.16 116.99 31.152 116.99 29.232V25.36H115.262V23.456H116.99V20.928H119.198V23.456H121.646V25.36H119.198V28.72C119.198 29.712 119.646 30.112 120.446 30.112C120.862 30.112 121.246 30.016 121.566 29.84ZM126.432 32.16C123.952 32.16 122.096 30.208 122.096 27.728C122.096 25.232 123.952 23.296 126.432 23.296C128.912 23.296 130.768 25.232 130.768 27.728C130.768 30.208 128.912 32.16 126.432 32.16ZM126.432 30.192C127.536 30.192 128.48 29.312 128.48 27.728C128.48 26.144 127.536 25.28 126.432 25.28C125.328 25.28 124.384 26.144 124.384 27.728C124.384 29.312 125.328 30.192 126.432 30.192ZM138.811 32.16C136.347 32.16 134.651 30.336 134.651 27.728C134.651 25.168 136.411 23.296 138.811 23.296C141.035 23.296 142.683 24.8 142.955 27.104H140.619C140.507 25.968 139.819 25.264 138.811 25.264C137.659 25.264 136.923 26.208 136.923 27.728C136.923 29.264 137.643 30.192 138.811 30.192C139.835 30.192 140.491 29.52 140.619 28.352H142.955C142.699 30.704 141.099 32.16 138.811 32.16ZM149.311 32V30.944C148.751 31.744 147.903 32.16 146.735 32.16C144.959 32.16 143.775 31.12 143.775 29.52C143.775 27.856 145.119 26.96 147.615 26.96C148.095 26.96 148.511 26.992 149.039 27.056V26.544C149.039 25.584 148.495 25.024 147.567 25.024C146.607 25.024 146.031 25.584 145.951 26.544H143.951C144.079 24.576 145.503 23.296 147.567 23.296C149.807 23.296 151.119 24.528 151.119 26.624V32H149.311ZM145.871 29.456C145.871 30.176 146.383 30.624 147.215 30.624C148.351 30.624 149.039 30 149.039 29.008V28.4C148.511 28.32 148.143 28.288 147.759 28.288C146.495 28.288 145.871 28.688 145.871 29.456ZM155.099 27.856V32H152.875V23.456H154.923V25.104C155.547 24.016 156.875 23.36 158.251 23.36V25.664C156.363 25.52 155.099 25.968 155.099 27.856ZM165.106 29.84V31.776C164.562 32.048 164.05 32.16 163.394 32.16C161.634 32.16 160.53 31.152 160.53 29.232V25.36H158.802V23.456H160.53V20.928H162.738V23.456H165.186V25.36H162.738V28.72C162.738 29.712 163.186 30.112 163.986 30.112C164.402 30.112 164.786 30.016 165.106 29.84Z'
                                    fill='#610FD1'
                                />
                                <defs>
                                    <filter
                                        id='filter0_d_840_2160'
                                        x='0'
                                        y='0'
                                        width='248'
                                        height='56'
                                        filterUnits='userSpaceOnUse'
                                        color-interpolation-filters='sRGB'
                                    >
                                        <feFlood flood-opacity='0' result='BackgroundImageFix' />
                                        <feColorMatrix
                                            in='SourceAlpha'
                                            type='matrix'
                                            values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                                            result='hardAlpha'
                                        />
                                        <feOffset dy='2' />
                                        <feGaussianBlur stdDeviation='2' />
                                        <feComposite in2='hardAlpha' operator='out' />
                                        <feColorMatrix
                                            type='matrix'
                                            values='0 0 0 0 0.498039 0 0 0 0 0.239216 0 0 0 0 0.854902 0 0 0 0.25 0'
                                        />
                                        <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_840_2160' />
                                        <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow_840_2160' result='shape' />
                                    </filter>
                                </defs>
                            </svg>
                        </button>
                    ) : (
                        <button className='local-plus-icon' onClick={this._SingleAddToCart}>
                            <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                <g clipPath='url(#clip0_840_2233)'>
                                    <path
                                        d='M10 0.771423V19.3429'
                                        stroke='#610FD1'
                                        strokeWidth='1.41697'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                    <path
                                        d='M0.714355 10H19.2858'
                                        stroke='#610FD1'
                                        strokeWidth='1.41697'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                </g>
                                <defs>
                                    <clipPath id='clip0_840_2233'>
                                        <rect width='20' height='20' fill='white' />
                                    </clipPath>
                                </defs>
                            </svg>
                        </button>
                    )}
                </>
            );
            return <Node className='nope'>{buttonAddtoCart}</Node>;
        }
        if (getQty === 1 && this.state.currentProductqQty === '') {
            button = (
                <>
                    {isBundleCollection ? (
                        <button className='bundle-decrement bundle-quantity__controls' onClick={this.removeITem}>
                            <svg width='248' height='56' viewBox='0 0 248 56' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                <g filter='url(#filter0_d_840_2206)'>
                                    <rect
                                        x='4'
                                        y='2'
                                        width='240'
                                        height='48'
                                        rx='24'
                                        fill='#610FD1'
                                        fill-opacity='0.9'
                                        shape-rendering='crispEdges'
                                    />
                                    <rect x='4.5' y='2.5' width='239' height='47' rx='23.5' stroke='#610FD1' shape-rendering='crispEdges' />
                                </g>
                                <g clip-path='url(#clip0_840_2206)'>
                                    <path d='M85 22.5H97' stroke='white' stroke-linecap='round' stroke-linejoin='round' />
                                    <path
                                        d='M86.5 22.5H95.5V31.5C95.5 31.7652 95.3946 32.0196 95.2071 32.2071C95.0196 32.3946 94.7652 32.5 94.5 32.5H87.5C87.2348 32.5 86.9804 32.3946 86.7929 32.2071C86.6054 32.0196 86.5 31.7652 86.5 31.5V22.5Z'
                                        stroke='white'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                    />
                                    <path
                                        d='M88.5 22.5V22C88.5 21.337 88.7634 20.7011 89.2322 20.2322C89.7011 19.7634 90.337 19.5 91 19.5C91.663 19.5 92.2989 19.7634 92.7678 20.2322C93.2366 20.7011 93.5 21.337 93.5 22V22.5'
                                        stroke='white'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                    />
                                </g>
                                <path
                                    d='M107.789 32H105.357V20.512H109.853C112.749 20.512 114.429 21.856 114.429 24.24C114.429 25.792 113.469 27.024 111.837 27.568L114.797 32H111.901L109.309 27.936H107.789V32ZM107.789 22.528V25.936H109.741C111.213 25.936 111.997 25.36 111.997 24.24C111.997 23.104 111.213 22.528 109.741 22.528H107.789ZM119.311 32.144C116.751 32.144 115.039 30.368 115.039 27.696C115.039 25.152 116.815 23.296 119.279 23.296C121.983 23.296 123.775 25.488 123.375 28.272H117.311C117.455 29.664 118.127 30.432 119.263 30.432C120.239 30.432 120.895 29.952 121.151 29.104H123.359C122.879 31.04 121.391 32.144 119.311 32.144ZM119.231 24.928C118.191 24.928 117.535 25.6 117.343 26.864H121.023C120.959 25.68 120.287 24.928 119.231 24.928ZM126.929 32H124.705V23.456H126.753V24.672C127.377 23.808 128.177 23.296 129.313 23.296C130.481 23.296 131.377 23.84 131.809 24.816C132.481 23.872 133.345 23.296 134.625 23.296C136.353 23.296 137.521 24.432 137.521 26.48V32H135.313V27.184C135.313 25.984 134.801 25.264 133.825 25.264C132.945 25.264 132.225 25.952 132.225 27.232V32H130.017V27.184C130.017 25.984 129.505 25.264 128.529 25.264C127.633 25.264 126.929 25.952 126.929 27.232V32ZM143.04 32.16C140.56 32.16 138.704 30.208 138.704 27.728C138.704 25.232 140.56 23.296 143.04 23.296C145.52 23.296 147.376 25.232 147.376 27.728C147.376 30.208 145.52 32.16 143.04 32.16ZM143.04 30.192C144.144 30.192 145.088 29.312 145.088 27.728C145.088 26.144 144.144 25.28 143.04 25.28C141.936 25.28 140.992 26.144 140.992 27.728C140.992 29.312 141.936 30.192 143.04 30.192ZM156.034 23.456L152.898 32H150.466L147.314 23.456H149.714L151.746 29.472L153.794 23.456H156.034ZM160.265 32.144C157.705 32.144 155.993 30.368 155.993 27.696C155.993 25.152 157.769 23.296 160.233 23.296C162.937 23.296 164.729 25.488 164.329 28.272H158.265C158.409 29.664 159.081 30.432 160.217 30.432C161.193 30.432 161.849 29.952 162.105 29.104H164.313C163.833 31.04 162.345 32.144 160.265 32.144ZM160.185 24.928C159.145 24.928 158.489 25.6 158.297 26.864H161.977C161.913 25.68 161.241 24.928 160.185 24.928Z'
                                    fill='white'
                                />
                                <defs>
                                    <filter
                                        id='filter0_d_840_2206'
                                        x='0'
                                        y='0'
                                        width='248'
                                        height='56'
                                        filterUnits='userSpaceOnUse'
                                        color-interpolation-filters='sRGB'
                                    >
                                        <feFlood flood-opacity='0' result='BackgroundImageFix' />
                                        <feColorMatrix
                                            in='SourceAlpha'
                                            type='matrix'
                                            values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                                            result='hardAlpha'
                                        />
                                        <feOffset dy='2' />
                                        <feGaussianBlur stdDeviation='2' />
                                        <feComposite in2='hardAlpha' operator='out' />
                                        <feColorMatrix
                                            type='matrix'
                                            values='0 0 0 0 0.498039 0 0 0 0 0.239216 0 0 0 0 0.854902 0 0 0 0.25 0'
                                        />
                                        <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_840_2206' />
                                        <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow_840_2206' result='shape' />
                                    </filter>
                                    <clipPath id='clip0_840_2206'>
                                        <rect width='14' height='14' fill='white' transform='translate(84 19)' />
                                    </clipPath>
                                </defs>
                            </svg>
                        </button>
                    ) : (
                        <button className='decrement quantity__controls' onClick={this.removeITem}>
                            <svg width='21' height='20' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                <g clipPath='url(#clip0_840_2209)'>
                                    <path d='M1 3.5H13' stroke='white' strokeLinecap='round' strokeLinejoin='round' />
                                    <path
                                        d='M2.5 3.5H11.5V12.5C11.5 12.7652 11.3946 13.0196 11.2071 13.2071C11.0196 13.3946 10.7652 13.5 10.5 13.5H3.5C3.23478 13.5 2.98043 13.3946 2.79289 13.2071C2.60536 13.0196 2.5 12.7652 2.5 12.5V3.5Z'
                                        stroke='white'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                    <path
                                        d='M4.5 3.5V3C4.5 2.33696 4.76339 1.70107 5.23223 1.23223C5.70107 0.763392 6.33696 0.5 7 0.5C7.66304 0.5 8.29893 0.763392 8.76777 1.23223C9.23661 1.70107 9.5 2.33696 9.5 3V3.5'
                                        stroke='white'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                </g>
                                <defs>
                                    <clipPath id='clip0_840_2209'>
                                        <rect width='21' height='20' fill='white' />
                                    </clipPath>
                                </defs>
                            </svg>
                        </button>
                    )}
                </>
            );
        }
        if (this.state.currentProductqQty === 1) {
            button = (
                <>
                    {isBundleCollection ? (
                        <button className='bundle-decrement bundle-quantity__controls' onClick={this.removeITem}>
                            <svg width='248' height='56' viewBox='0 0 248 56' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                <g filter='url(#filter0_d_840_2206)'>
                                    <rect
                                        x='4'
                                        y='2'
                                        width='240'
                                        height='48'
                                        rx='24'
                                        fill='#610FD1'
                                        fill-opacity='0.9'
                                        shape-rendering='crispEdges'
                                    />
                                    <rect x='4.5' y='2.5' width='239' height='47' rx='23.5' stroke='#610FD1' shape-rendering='crispEdges' />
                                </g>
                                <g clip-path='url(#clip0_840_2206)'>
                                    <path d='M85 22.5H97' stroke='white' stroke-linecap='round' stroke-linejoin='round' />
                                    <path
                                        d='M86.5 22.5H95.5V31.5C95.5 31.7652 95.3946 32.0196 95.2071 32.2071C95.0196 32.3946 94.7652 32.5 94.5 32.5H87.5C87.2348 32.5 86.9804 32.3946 86.7929 32.2071C86.6054 32.0196 86.5 31.7652 86.5 31.5V22.5Z'
                                        stroke='white'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                    />
                                    <path
                                        d='M88.5 22.5V22C88.5 21.337 88.7634 20.7011 89.2322 20.2322C89.7011 19.7634 90.337 19.5 91 19.5C91.663 19.5 92.2989 19.7634 92.7678 20.2322C93.2366 20.7011 93.5 21.337 93.5 22V22.5'
                                        stroke='white'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                    />
                                </g>
                                <path
                                    d='M107.789 32H105.357V20.512H109.853C112.749 20.512 114.429 21.856 114.429 24.24C114.429 25.792 113.469 27.024 111.837 27.568L114.797 32H111.901L109.309 27.936H107.789V32ZM107.789 22.528V25.936H109.741C111.213 25.936 111.997 25.36 111.997 24.24C111.997 23.104 111.213 22.528 109.741 22.528H107.789ZM119.311 32.144C116.751 32.144 115.039 30.368 115.039 27.696C115.039 25.152 116.815 23.296 119.279 23.296C121.983 23.296 123.775 25.488 123.375 28.272H117.311C117.455 29.664 118.127 30.432 119.263 30.432C120.239 30.432 120.895 29.952 121.151 29.104H123.359C122.879 31.04 121.391 32.144 119.311 32.144ZM119.231 24.928C118.191 24.928 117.535 25.6 117.343 26.864H121.023C120.959 25.68 120.287 24.928 119.231 24.928ZM126.929 32H124.705V23.456H126.753V24.672C127.377 23.808 128.177 23.296 129.313 23.296C130.481 23.296 131.377 23.84 131.809 24.816C132.481 23.872 133.345 23.296 134.625 23.296C136.353 23.296 137.521 24.432 137.521 26.48V32H135.313V27.184C135.313 25.984 134.801 25.264 133.825 25.264C132.945 25.264 132.225 25.952 132.225 27.232V32H130.017V27.184C130.017 25.984 129.505 25.264 128.529 25.264C127.633 25.264 126.929 25.952 126.929 27.232V32ZM143.04 32.16C140.56 32.16 138.704 30.208 138.704 27.728C138.704 25.232 140.56 23.296 143.04 23.296C145.52 23.296 147.376 25.232 147.376 27.728C147.376 30.208 145.52 32.16 143.04 32.16ZM143.04 30.192C144.144 30.192 145.088 29.312 145.088 27.728C145.088 26.144 144.144 25.28 143.04 25.28C141.936 25.28 140.992 26.144 140.992 27.728C140.992 29.312 141.936 30.192 143.04 30.192ZM156.034 23.456L152.898 32H150.466L147.314 23.456H149.714L151.746 29.472L153.794 23.456H156.034ZM160.265 32.144C157.705 32.144 155.993 30.368 155.993 27.696C155.993 25.152 157.769 23.296 160.233 23.296C162.937 23.296 164.729 25.488 164.329 28.272H158.265C158.409 29.664 159.081 30.432 160.217 30.432C161.193 30.432 161.849 29.952 162.105 29.104H164.313C163.833 31.04 162.345 32.144 160.265 32.144ZM160.185 24.928C159.145 24.928 158.489 25.6 158.297 26.864H161.977C161.913 25.68 161.241 24.928 160.185 24.928Z'
                                    fill='white'
                                />
                                <defs>
                                    <filter
                                        id='filter0_d_840_2206'
                                        x='0'
                                        y='0'
                                        width='248'
                                        height='56'
                                        filterUnits='userSpaceOnUse'
                                        color-interpolation-filters='sRGB'
                                    >
                                        <feFlood flood-opacity='0' result='BackgroundImageFix' />
                                        <feColorMatrix
                                            in='SourceAlpha'
                                            type='matrix'
                                            values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                                            result='hardAlpha'
                                        />
                                        <feOffset dy='2' />
                                        <feGaussianBlur stdDeviation='2' />
                                        <feComposite in2='hardAlpha' operator='out' />
                                        <feColorMatrix
                                            type='matrix'
                                            values='0 0 0 0 0.498039 0 0 0 0 0.239216 0 0 0 0 0.854902 0 0 0 0.25 0'
                                        />
                                        <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_840_2206' />
                                        <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow_840_2206' result='shape' />
                                    </filter>
                                    <clipPath id='clip0_840_2206'>
                                        <rect width='14' height='14' fill='white' transform='translate(84 19)' />
                                    </clipPath>
                                </defs>
                            </svg>
                        </button>
                    ) : (
                        <button className='decrement quantity__controls' onClick={this.removeITem}>
                            <svg width='21' height='20' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                <g clipPath='url(#clip0_840_2209)'>
                                    <path d='M1 3.5H13' stroke='white' strokeLinecap='round' strokeLinejoin='round' />
                                    <path
                                        d='M2.5 3.5H11.5V12.5C11.5 12.7652 11.3946 13.0196 11.2071 13.2071C11.0196 13.3946 10.7652 13.5 10.5 13.5H3.5C3.23478 13.5 2.98043 13.3946 2.79289 13.2071C2.60536 13.0196 2.5 12.7652 2.5 12.5V3.5Z'
                                        stroke='white'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                    <path
                                        d='M4.5 3.5V3C4.5 2.33696 4.76339 1.70107 5.23223 1.23223C5.70107 0.763392 6.33696 0.5 7 0.5C7.66304 0.5 8.29893 0.763392 8.76777 1.23223C9.23661 1.70107 9.5 2.33696 9.5 3V3.5'
                                        stroke='white'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                </g>
                                <defs>
                                    <clipPath id='clip0_840_2209'>
                                        <rect width='21' height='20' fill='white' />
                                    </clipPath>
                                </defs>
                            </svg>
                        </button>
                    )}
                </>
            );
        }
        return (
            <Node className='nope localy-quantity'>
                <div
                    className='quantity'
                    id={`msc-cart-line__quantity_${this.props.product.RecordId}-${this.props.product.RecordId}-${this.props.product.ItemId}`}
                >
                    {button}
                    {!isBundleCollection && (
                        <IncrementalQuantity
                            id={`msc-cart-line__quantity_${this.props.product.RecordId}-${this.props.product.RecordId}-${this.props.product.ItemId}`}
                            //id={`msc-cart-line__quantity_`}
                            max={20}
                            currentCount={this.state.currentProductqQty || getQty}
                            onChange={this.onChangeHandler}
                            inputQuantityAriaLabel={'resources.inputQuantityAriaLabel'}
                            decrementButtonAriaLabel={'extentionResources.decrementButtonAriaLabel'}
                            incrementButtonAriaLabel={'extentionResources.incrementButtonAriaLabel'}
                            minQuantityText={'extentionResources.minQuantityText'}
                            maxQuantityText={'extentionResources.maxQuantityText'}
                            itemId={this.props.product.ItemId}
                            //   telemetryContent={telemetryContent}
                        />
                    )}
                </div>
            </Node>
        );
    }
}
export default ProductCollectionQty;
